import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  GlobalMessageService,
  GlobalMessageType,
  RoutingService,
  WindowRef,
  UserAddressService,
} from '@spartacus/core';
import { map, mergeMap, catchError, switchMap } from 'rxjs/operators';
import { of, from } from 'rxjs';
import { OCC_USER_ID_CURRENT } from '@spartacus/core';
import { KnBrCommonService } from 'src/app/services/kn-br-common.service';

import { CustomerData } from '../../models/customer.model';
import {
  KnBrCartAddUpdateContext,
  KnBrCartResetContext,
} from '../kn-br-cart-context/kn-br-cart-context.actions';
import { KnBrCartContextAdapter } from '../kn-br-cart-context/kn-br-cart-context.adapter';
import { KnBrDraftOrdersService } from '../kn-br-draft-orders/kn-br-draft-orders.service';
import { KnBrQuoteRemoveContext } from '../kn-br-quote-context/kn-br-quote-context.actions';
import { KnBrQuotesService } from '../kn-br-quotes/kn-br-quotes.service';
import {
  KnBrCustomerAddUpdateContext,
  KnBrCustomerContextActions,
  KnBrCustomerContextActionTypes,
  KnBrCustomerLoadSuccessContext,
  KnBrCustomerRemoveContext,
  KnBrCustomerResetContext,
} from './kn-br-customer-context.actions';
import { KnBrCustomerContextAdapter } from './kn-br-customer-context.adapter';
import { CartConnector } from '@spartacus/cart/base/core';

@Injectable()
export class KnBrCustomerContextEffects {
loadCustomerContext$ = createEffect(() =>
  this.actions$.pipe(
    ofType(KnBrCustomerContextActionTypes.KnBrCustomerContextLoadAction),
    map((x) => {
      const context = this.knBrCustomerContextAdapter.get();
      return new KnBrCustomerLoadSuccessContext(context);
    })
  )
);

addUpdateCustomerContext$ = createEffect(() =>
  this.actions$.pipe(
    ofType(KnBrCustomerContextActionTypes.KnBrCustomerContextAddUpdateAction),
    map((action: KnBrCustomerAddUpdateContext) => action.payload),
    mergeMap((customer: CustomerData) => {
      const copyObject = JSON.parse(this.winRef.localStorage.getItem('copyObject'));
      this.winRef.localStorage.removeItem('copyObject');
      if (copyObject) {
        if (copyObject.isQuote) {
          this.knBrQuoteService.copy({ referenceQuoteId: copyObject.code });
        } else {
          this.knBrDraftOrderService.copy({ referenceCartId: copyObject.code, name: copyObject?.name });
        }
        return [];
      }
      const redirectUrl = JSON.parse(this.winRef.localStorage.getItem('redirect'));
      this.winRef.localStorage.removeItem('redirect');
      if (redirectUrl) {
        this.routingService.go({ cxRoute: redirectUrl.route, params: redirectUrl.params });
      }
      const context = this.getCustomerContext();
      if (!context || context.customerNumber !== customer.customerNumber) {
        const previousCustomerNumber = context?.customerNumber;
        const activeCartContext = this.knBrCartContextAdapter.get();

        if (previousCustomerNumber && previousCustomerNumber !== customer.customerNumber) {
          this.knBrCartContextAdapter.setForCustomer(previousCustomerNumber, activeCartContext);
        }

        this.knBrCustomerContextAdapter.set(customer);
        // Change done for sloan
        this.routingService.go({ cxRoute: 'customer360' });
        this.messageService.add({ key: 'customer.context.set' }, GlobalMessageType.MSG_TYPE_CONFIRMATION);
        this.knBrCommonService.setRecentCustomer(customer.customerNumber);
        this.userAddressService.loadAddresses();
        this.knBrCommonService.getBrandSalesShipToList(customer.customerNumber);

        // Always fetch from server for this customer (do not use localStorage cached cart)
        return this.cartConnector.loadAll(customer.customerNumber).pipe(
          map((carts) => {
            const active = carts?.find((c) => c && c.saveTime === undefined);
            if (active && active.code) {
              // update context with active cart from server
              this.knBrCartContextAdapter.setForCustomer(customer.customerNumber, active.code);
              return [new KnBrQuoteRemoveContext(), new KnBrCartAddUpdateContext(active.code)];
            }
            return [new KnBrQuoteRemoveContext(), new KnBrCartResetContext()];
          }),
          switchMap((actions) => from(actions)),
          catchError(() => from([new KnBrQuoteRemoveContext(), new KnBrCartResetContext()]))
        );
      }
      return [];
    })
  )
);

removeCustomerContext$ = createEffect(() =>
  this.actions$.pipe(
    ofType(KnBrCustomerContextActionTypes.KnBrCustomerContextRemoveAction),
    map((action: KnBrCustomerRemoveContext) => action.payload),
    mergeMap((customer: any) => {
      this.knBrCustomerContextAdapter.remove();
      // this.routingService.go({ cxRoute: 'home' });
      this.messageService.add({ key: 'customer.context.remove' }, GlobalMessageType.MSG_TYPE_CONFIRMATION);
      return [new KnBrQuoteRemoveContext(), new KnBrCartResetContext()];
    })
  )
);

resetCustomerContext$ = createEffect(() =>
  this.actions$.pipe(
    ofType(KnBrCustomerContextActionTypes.KnBrCustomerContextResetAction),
    map((action: KnBrCustomerResetContext) => action.payload),
    mergeMap((customer: any) => {
      this.knBrCustomerContextAdapter.remove();
      return [new KnBrQuoteRemoveContext(), new KnBrCartResetContext()];
    })
  )
);


  getCustomerContext() {
    return this.knBrCustomerContextAdapter.get();
  }

  constructor(
    private actions$: Actions<KnBrCustomerContextActions>,
    private messageService: GlobalMessageService,
    private knBrCustomerContextAdapter: KnBrCustomerContextAdapter,
    private knBrCartContextAdapter: KnBrCartContextAdapter,
    private routingService: RoutingService,
    private winRef: WindowRef,
    private knBrQuoteService: KnBrQuotesService,
    private knBrDraftOrderService: KnBrDraftOrdersService,
    private knBrCommonService: KnBrCommonService,
    protected userAddressService: UserAddressService,
    private cartConnector: CartConnector
  ) {}
}
